// Value imports are RELATIVE: builder-service reaches this file (the onboarding theme switch,
// `onboardingThemeCopy.ts`), and its runtime has no `@modules/*` resolution (CLAUDE.md, Guard 4).
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { normalizeThemeV2 } from '../../screen-utils/v2-conformance';
import { createDefaultFlowDocument } from '../factories/defaultStepNodeFactory';
import {
  DEFAULT_ACTION_BAR,
  DEFAULT_BACKDROP,
  DEFAULT_HEADER_BAR,
  DEFAULT_THEME_V2_COLORS,
  DEFAULT_THEME_V2_FONTS,
  DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS,
} from '../types/themesV2.types';
import type { CreateThemeV2Input, ThemeV2 } from '@modules/themes/types/themesV2.types';

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export interface CreateEditableThemeV2FromDraftOptions {
  baseTheme?: ThemeV2 | null;
  fallbackId?: string;
  fallbackName?: string;
  preserveBaseName?: boolean;
  now?: string;
}

export function createEditableThemeV2FromDraft(
  draft: CreateThemeV2Input,
  options: CreateEditableThemeV2FromDraftOptions = {}
): ThemeV2 {
  const baseTheme = options.baseTheme ? cloneJson(options.baseTheme) : null;
  const now = options.now ?? new Date().toISOString();
  const name = options.preserveBaseName && baseTheme?.name
    ? baseTheme.name
    : draft.name ?? baseTheme?.name ?? options.fallbackName ?? 'Generated Theme';

  const theme: ThemeV2 = {
    id: baseTheme?.id ?? options.fallbackId ?? 'theme-v2-generated-draft',
    name,
    colors: cloneJson(draft.colors ?? baseTheme?.colors ?? DEFAULT_THEME_V2_COLORS),
    fonts: cloneJson(draft.fonts ?? baseTheme?.fonts ?? DEFAULT_THEME_V2_FONTS),
    optionsListSettings: cloneJson(
      draft.optionsListSettings ?? baseTheme?.optionsListSettings ?? DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS
    ),
    background: cloneJson(draft.background ?? baseTheme?.background ?? DEFAULT_BACKDROP),
    headerBar: cloneJson(draft.headerBar ?? baseTheme?.headerBar ?? DEFAULT_HEADER_BAR),
    actionBar: cloneJson(draft.actionBar ?? baseTheme?.actionBar ?? DEFAULT_ACTION_BAR),
    flowDocument: cloneJson(draft.flowDocument ?? baseTheme?.flowDocument ?? createDefaultFlowDocument()),
    createdAt: baseTheme?.createdAt ?? now,
    updatedAt: now,
  };

  const layoutSettings = draft.layoutSettings ?? baseTheme?.layoutSettings;
  if (layoutSettings) {
    theme.layoutSettings = cloneJson(layoutSettings);
  }

  const webViewBackgroundColor = draft.webViewBackgroundColor !== undefined
    ? draft.webViewBackgroundColor
    : baseTheme?.webViewBackgroundColor;
  if (webViewBackgroundColor !== undefined) {
    theme.webViewBackgroundColor = webViewBackgroundColor;
  }

  const animationSettings = draft.animationSettings ?? baseTheme?.animationSettings;
  if (animationSettings) {
    theme.animationSettings = cloneJson(animationSettings);
  }

  const generatedMetadata = draft.generatedMetadata ?? baseTheme?.generatedMetadata;
  if (generatedMetadata) {
    theme.generatedMetadata = cloneJson(generatedMetadata);
  }

  if (draft.createdBy ?? baseTheme?.createdBy) {
    theme.createdBy = draft.createdBy ?? baseTheme?.createdBy;
  }
  const order = draft.order ?? baseTheme?.order;
  if (order !== undefined) {
    theme.order = order;
  }

  // Conformance guard: clone / generate-from-image / figma / onboarding→theme all
  // funnel here and copy draft/baseTheme structures VERBATIM. Repair any legacy
  // paths/shapes now so a newly-created theme never re-introduces the V2 leaks
  // that break propagation. Clean input passes through structurally identical.
  // See modules/schema-v2/v2-conformance-migration-table.md.
  return normalizeThemeV2(theme).content;
}

export function onboardingSettingsV2ToThemeV2(
  settings: OnboardingThemeSettingsV2,
  options: {
    id?: string;
    name?: string;
    now?: string;
  } = {}
): ThemeV2 {
  const now = options.now ?? new Date().toISOString();
  return createEditableThemeV2FromDraft(settings, {
    fallbackId: options.id ?? 'theme-editor-temp',
    fallbackName: options.name ?? 'Theme Settings',
    now,
  });
}

export function themeV2ToOnboardingSettings(
  theme: ThemeV2,
  options: {
    sourceThemeId?: string | null;
  } = {}
): OnboardingThemeSettingsV2 {
  const settings: OnboardingThemeSettingsV2 = {
    colors: cloneJson(theme.colors),
    fonts: cloneJson(theme.fonts),
    optionsListSettings: cloneJson(theme.optionsListSettings),
    background: cloneJson(theme.background),
    headerBar: cloneJson(theme.headerBar),
    actionBar: cloneJson(theme.actionBar),
  };

  // A legacy theme may lack templates; the onboarding then reads its default ones (as for a project theme).
  if (theme.flowDocument) {
    settings.flowDocument = cloneJson(theme.flowDocument);
  }
  if (theme.layoutSettings) {
    settings.layoutSettings = cloneJson(theme.layoutSettings);
  }
  if (theme.webViewBackgroundColor !== undefined) {
    settings.webViewBackgroundColor = theme.webViewBackgroundColor;
  }
  if (theme.animationSettings) {
    settings.animationSettings = cloneJson(theme.animationSettings);
  }
  if (options.sourceThemeId) {
    settings.sourceThemeId = options.sourceThemeId;
  }
  if (theme.generatedMetadata) {
    settings.generatedMetadata = cloneJson(theme.generatedMetadata);
  }

  return settings;
}

export function cloneOnboardingThemeSettings(
  settings: OnboardingThemeSettingsV2,
  options: {
    sourceThemeId?: string | null;
  } = {}
): OnboardingThemeSettingsV2 {
  const snapshot: OnboardingThemeSettingsV2 = {
    colors: cloneJson(settings.colors),
    fonts: cloneJson(settings.fonts),
    optionsListSettings: cloneJson(settings.optionsListSettings),
    background: cloneJson(settings.background),
    headerBar: cloneJson(settings.headerBar),
    actionBar: cloneJson(settings.actionBar),
  };

  if (settings.layoutSettings) {
    snapshot.layoutSettings = cloneJson(settings.layoutSettings);
  }
  if (settings.webViewBackgroundColor !== undefined) {
    snapshot.webViewBackgroundColor = settings.webViewBackgroundColor;
  }
  if (settings.animationSettings) {
    snapshot.animationSettings = cloneJson(settings.animationSettings);
  }
  if (settings.flowDocument) {
    snapshot.flowDocument = cloneJson(settings.flowDocument);
  }
  const sourceThemeId = options.sourceThemeId ?? settings.sourceThemeId;
  if (sourceThemeId) {
    snapshot.sourceThemeId = sourceThemeId;
  }
  if (settings.generatedMetadata) {
    snapshot.generatedMetadata = cloneJson(settings.generatedMetadata);
  }

  return snapshot;
}
